import React, { useEffect, useMemo, useState } from "react";
import FormatSwitch, { DataFormat, toJson, toYaml } from "./FormatSwitch";
import { YamlLine } from "./YamlView";
import ReactDiffViewer, { DiffMethod, ReactDiffViewerStylesOverride, ReactDiffViewerProps } from 'react-diff-viewer-continued';
import "./DiffViewer.css";

/** how structured data is written out to be compared */
export type DiffFormat = DataFormat;

interface DiffViewerProps extends Omit<ReactDiffViewerProps, 'compareMethod' | 'oldValue' | 'newValue'> {
    /**
     * The two sides. Data — an object, an array, or a string of JSON — is
     * written out in `format` before it is compared. Any other string is text,
     * compared as it is. Nothing (null, undefined, '') is an empty side.
     */
    oldValue?: unknown;
    newValue?: unknown;
    /**
     * How data is shown when the diff opens: 'json' (default) or 'yaml'.
     * With `enableYaml`, this is where the switch starts.
     */
    format?: DiffFormat;
    /**
     * A JSON / YAML switch for the reader, at the right end of the titles.
     * Off by default; never shown for text, which has no other format.
     */
    enableYaml?: boolean;
    /**
     * Comparison method: 'lines', 'words', 'chars' (default: 'lines')
     * Simplified version of ReactDiffViewer's compareMethod prop
     */
    compareMethod?: 'lines' | 'words' | 'chars';
}

type Side =
    | { kind: 'empty' }
    | { kind: 'data'; value: unknown }
    | { kind: 'text'; value: string };

/** what a side is: data to be written out, text to be shown as is, or nothing */
const readSide = (value: unknown): Side => {
    if (value === null || value === undefined) return { kind: 'empty' };
    if (typeof value !== 'string') return { kind: 'data', value };
    if (value.trim() === '') return { kind: 'empty' };
    try {
        return { kind: 'data', value: JSON.parse(value) };
    } catch {
        return { kind: 'text', value };
    }
};

/** a side with nothing on it — null, undefined, '' — as the diff reads one */
export const isEmptySide = (value: unknown) => readSide(value).kind === 'empty';

const renderYaml = (source: string) => <YamlLine text={source} />;

const writeSide = (side: Side, format: DiffFormat, asText: boolean) => {
    if (side.kind === 'empty') return '';
    if (side.kind === 'text') return side.value;
    // beside a text side, data is shown as JSON text too, so the two compare
    if (asText) return toJson(side.value);
    return format === 'yaml' ? toYaml(side.value) : toJson(side.value);
};

/**
 * The one diff in the app: two versions of something, side by side or unified,
 * line by line.
 *
 * Hand it data and it writes it out for you — JSON by default, or YAML with
 * `format="yaml"` — and `enableYaml` puts a switch in its title row to flip
 * between the two. Hand it text and it compares the text.
 *
 * Features:
 * - Side-by-side or unified comparison view; one column when a side is
 *   empty (something created or removed), all of it added or removed
 * - Line, word, or character-level diffing
 * - JSON or YAML for data, switchable by the reader
 * - Responsive design (automatically switches to unified view on mobile)
 * - Customizable titles and appearance
 * - Mobile-friendly with text wrapping
 * - No horizontal scroll
 *
 * @example
 * ```tsx
 * // Data, shown as JSON
 * <DiffViewer oldValue={oldDeclaration} newValue={newDeclaration} />
 *
 * // Data, opening on YAML, with the switch to go back to JSON
 * <DiffViewer oldValue={oldDeclaration} newValue={newDeclaration} format="yaml" enableYaml />
 *
 * // Text, with custom titles
 * <DiffViewer oldValue={oldCode} newValue={newCode} leftTitle="Version 1.0" rightTitle="Version 2.0" />
 *
 * // Unified view with word diff
 * <DiffViewer oldValue={text1} newValue={text2} splitView={false} compareMethod="words" />
 * ```
 */
const DiffViewer = React.memo(
    function DiffViewer({
                            oldValue,
                            newValue,
                            format = 'json',
                            enableYaml = false,
                            compareMethod = 'lines',
                            splitView,
                            styles,
                            leftTitle = 'Original',
                            rightTitle = 'Updated',
                            ...otherProps
                        }: DiffViewerProps) {

        const [shown, setShown] = useState<DiffFormat>(format);
        // a page that changes its default is followed; the reader's own pick
        // stands until it does
        useEffect(() => setShown(format), [format]);

        const oldSide = useMemo(() => readSide(oldValue), [oldValue]);
        const newSide = useMemo(() => readSide(newValue), [newValue]);
        // a format only means something for data: with text on either side,
        // there is nothing to write out and no switch to offer
        const asText = oldSide.kind === 'text' || newSide.kind === 'text';
        const hasData = oldSide.kind === 'data' || newSide.kind === 'data';

        const oldText = useMemo(() => writeSide(oldSide, shown, asText), [oldSide, shown, asText]);
        const newText = useMemo(() => writeSide(newSide, shown, asText), [newSide, shown, asText]);

        // Auto-detect mobile and use unified view
        const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
        // one side empty — something created, or removed — has nothing to set
        // beside it: one column, all added or all removed, under the title of
        // the side that is there
        const oneSided = (oldSide.kind === 'empty') !== (newSide.kind === 'empty');
        const shouldSplitView = !oneSided && (splitView !== undefined ? splitView : !isMobile);
        const unifiedTitle = oneSided && oldSide.kind === 'empty' ? rightTitle : leftTitle;

        // Map compare method to DiffMethod enum
        const diffMethod = compareMethod === 'words'
            ? DiffMethod.WORDS
            : compareMethod === 'chars'
                ? DiffMethod.CHARS
                : DiffMethod.LINES;

        // Default styles for mobile-friendly display, with GitLab's diff
        // colours: lines and the words changed in them (variables.scss there)
        const defaultStyles: ReactDiffViewerStylesOverride = {
            variables: {
                light: {
                    addedBackground: '#ecfdf0',
                    addedGutterBackground: '#ddfbe6',
                    wordAddedBackground: '#c7f0d2',
                    removedBackground: '#fbe9eb',
                    removedGutterBackground: '#f9d7dc',
                    wordRemovedBackground: '#fac5cd',
                },
            },
            contentText: {
                wordBreak: 'break-word',
                wordWrap: 'break-word',
                whiteSpace: 'pre-wrap',
                overflowWrap: 'break-word',
            },
            line: {
                wordBreak: 'break-word',
                wordWrap: 'break-word',
                whiteSpace: 'pre-wrap',
                overflowWrap: 'break-word',
            }
        };

        // Merge default styles with custom styles
        const mergedStyles: ReactDiffViewerStylesOverride = styles
            ? {
                ...defaultStyles,
                ...styles,
                variables: { light: { ...defaultStyles.variables?.light, ...styles.variables?.light }, dark: styles.variables?.dark },
                contentText: { ...(defaultStyles.contentText as any), ...(styles.contentText as any) },
                line: { ...(defaultStyles.line as any), ...(styles.line as any) }
            }
            : defaultStyles;

        // The switch sits in the title row, at its right end, rather than on a
        // line of its own above the diff: in the right-hand title side by side,
        // in the one title a unified diff has otherwise.
        const formatSwitch = enableYaml && hasData && !asText
            ? <FormatSwitch value={shown} onChange={setShown} />
            : null;
        const withSwitch = (title?: string | React.ReactElement): string | React.ReactElement | undefined => (formatSwitch ? (
            <span className="nx-diff__title">
                <span className="nx-diff__title-text">{title}</span>
                {formatSwitch}
            </span>
        ) : title);

        return (
            <div className="nx-diff">
                <ReactDiffViewer
                    {...otherProps}
                    oldValue={oldText}
                    newValue={newText}
                    splitView={shouldSplitView}
                    leftTitle={shouldSplitView ? leftTitle : withSwitch(unifiedTitle)}
                    rightTitle={shouldSplitView ? withSwitch(rightTitle) : rightTitle}
                    compareMethod={diffMethod}
                    styles={mergedStyles}
                    // YAML in the colours the rest of the app gives it (YamlView)
                    renderContent={shown === 'yaml' && hasData && !asText ? renderYaml : otherProps.renderContent}
                />
            </div>
        );
    },
    (prevProps, nextProps) => {
        // Deep comparison of all props
        return JSON.stringify(prevProps) === JSON.stringify(nextProps);
    }
);

export default DiffViewer;
